import {
  Assessment as AssessmentIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Help as HelpIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { DeepResearchReport } from "../../../types/researchWorkflow";

interface DeepResearchReportViewProps {
  report: DeepResearchReport;
  entityName: string;
  onClose: () => void;
}

const DeepResearchReportView: React.FC<DeepResearchReportViewProps> = ({
  report,
  entityName,
  onClose,
}) => {
  const getConfidenceColor = (confidence: "low" | "medium" | "high") => {
    switch (confidence) {
      case "high":
        return "success";
      case "medium":
        return "warning";
      case "low":
        return "error";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return "#4caf50";
    if (score >= 6) return "#ff9800";
    return "#f44336";
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AssessmentIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography variant="body2" fontWeight="600">
              Deep Research Report
            </Typography>
          </Box>
          <Tooltip title="Close Report">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {entityName}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {/* Executive Summary */}
        <Paper
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "rgba(33, 150, 243, 0.1)",
            border: 1,
            borderColor: "primary.main",
          }}
        >
          <Typography
            variant="caption"
            color="primary.main"
            fontWeight="600"
            gutterBottom
            display="block"
          >
            EXECUTIVE SUMMARY
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            {report.executiveSummary}
          </Typography>
        </Paper>

        {/* Criterion Scores */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="600"
            gutterBottom
            display="block"
            sx={{ mb: 1.5 }}
          >
            CRITERION EVALUATION
          </Typography>

          {report.criterionScores.map((criterion, index) => (
            <Paper
              key={criterion.criterionId}
              variant="outlined"
              sx={{ p: 1.5, mb: 1.5, backgroundColor: "#1e1e1e" }}
            >
              {/* Criterion Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight="600">
                  {criterion.criterionLabel}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={criterion.confidence}
                    size="small"
                    color={getConfidenceColor(criterion.confidence)}
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                  <Chip
                    label={`${criterion.score}/10`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: getScoreColor(criterion.score),
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>

              {/* Score Bar */}
              <LinearProgress
                variant="determinate"
                value={criterion.score * 10}
                sx={{
                  mb: 1,
                  height: 6,
                  borderRadius: 1,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: getScoreColor(criterion.score),
                  },
                }}
              />

              {/* Evidence */}
              {criterion.evidence.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {criterion.evidence.map((evidence, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        gap: 1,
                        mb: 0.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <CheckIcon
                        sx={{
                          fontSize: 14,
                          color: "success.main",
                          mt: 0.2,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.4 }}
                      >
                        {evidence}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          ))}
        </Box>

        {/* Information Gaps */}
        {report.gaps.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="600"
              gutterBottom
              display="block"
              sx={{ mb: 1 }}
            >
              INFORMATION GAPS
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, backgroundColor: "rgba(255, 152, 0, 0.05)" }}
            >
              {report.gaps.map((gap, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: idx < report.gaps.length - 1 ? 1 : 0,
                    alignItems: "flex-start",
                  }}
                >
                  <ErrorIcon
                    sx={{
                      fontSize: 14,
                      color: "warning.main",
                      mt: 0.2,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.4 }}
                  >
                    {gap}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Box>
        )}

        {/* Unknowns */}
        {report.unknowns.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="600"
              gutterBottom
              display="block"
              sx={{ mb: 1 }}
            >
              UNKNOWNS
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, backgroundColor: "rgba(158, 158, 158, 0.05)" }}
            >
              {report.unknowns.map((unknown, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: idx < report.unknowns.length - 1 ? 1 : 0,
                    alignItems: "flex-start",
                  }}
                >
                  <HelpIcon
                    sx={{
                      fontSize: 14,
                      color: "text.disabled",
                      mt: 0.2,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.4 }}
                  >
                    {unknown}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Box>
        )}
      </Box>

      {/* Footer Stats */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: "#1e1e1e",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">
            Last Updated
          </Typography>
          <Typography variant="caption" fontWeight="600">
            {new Date(report.lastRun).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DeepResearchReportView;
